import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Info, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { ROLE } from '@/lib/constants';

const MANAGEMENT_LEVELS = ['Management 1', 'Management 2', 'Management 3', 'Project SPOC'];
const REPORTS_TO_LEVEL: Record<string, string> = { 'Management 3': 'Management 2', 'Management 2': 'Management 1' };

interface ManagementAccessEntry {
  email: string;
  managementLevel: string;
  managerEmail?: string;
  addedBy?: string;
  addedAt?: string;
}

export default function ManagementAccessPage() {
  const { appId } = useParams<{ appId: string }>();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState<ManagementAccessEntry[]>([]);
  const [userData, setUserData] = useState<any>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Form state
  const [email, setEmail] = useState('');
  const [managementLevel, setManagementLevel] = useState('');
  const [managerEmail, setManagerEmail] = useState('');
  const [activeLevel, setActiveLevel] = useState<string | null>(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [appId]);

  const fetchUserData = async () => {
    try {
      const response = await sentinelNetwork.get('/entitlements');
      setUserData(response.data);
    } catch (err) {
      console.error('Failed to fetch user data:', err);
    }
  };

  const fetchEntries = async () => {
    if (!appId) return;
    setLoading(true);
    try {
      const response = await sentinelNetwork.get(`/config/project/${appId}/management-access`);
      setEntries(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to fetch management access:', err);
    } finally {
      setLoading(false);
    }
  };

  const currentUserRoles: string[] = userData?.roles || [];
  const isAdmin = [ROLE.SENTINEL_PROJECT_CREATION, ROLE.SENTINEL_POWER_USER].some((r) => currentUserRoles.includes(r));

  const ALLOWED_EMAIL_DOMAINS = ['@mercedes-benz.com', '@msc.corpdir.net'];

  const duplicateEntry = entries.some(
    (e) => e.email?.toLowerCase() === email.trim().toLowerCase()
  );

  const isEmailValid =
    email.trim() === '' ||
    ALLOWED_EMAIL_DOMAINS.some((domain) =>
      email.trim().toLowerCase().endsWith(domain)
    );

  const visibleEntries = activeLevel
    ? entries.filter(e => e.managementLevel === activeLevel)
    : entries;
  const filterableLevels = ['Management 1', 'Management 2', 'Management 3'];
  const levels = [...new Set(entries.map(e => e.managementLevel))].filter(level => filterableLevels.includes(level));

  const handleAdd = async () => {
    if (!email.trim() || !managementLevel) return;
    if (REPORTS_TO_LEVEL[managementLevel] && !managerEmail) return;

    setSaving(true);
    try {
      await sentinelNetwork.post(`/config/project/${appId}/management-access`, {
        email: email.trim().toLowerCase(),
        managementLevel,
        ...(REPORTS_TO_LEVEL[managementLevel] && managerEmail && { managerEmail }),
      });

      setSnackbar({
        open: true,
        message: 'Management access added successfully',
        severity: 'success',
      });

      await fetchEntries();
      setEmail('');
      setManagementLevel('');
      setManagerEmail('');
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to add management access',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (entry: ManagementAccessEntry) => {
    try {
      await sentinelNetwork.delete(`/config/project/${appId}/management-access/${encodeURIComponent(entry.email)}`);

      setSnackbar({
        open: true,
        message: 'Management access removed',
        severity: 'success',
      });

      await fetchEntries();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: 'Failed to remove management access',
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        height="100%"
        width="100%"
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 1 }}
          >
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
              }}
            >
              <ShieldCheck size={24} color={theme.palette.primary.main} />
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h5" fontWeight={700}>
                Management Access
              </Typography>
              <Tooltip title="Management must raise a SENTINEL_USERS role in order to access the project." arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'default', color: 'text.secondary' }}>
                  <Info size={16} />
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Manage escalation levels and special access for this project
          </Typography>
        </Box>

        {/* Add Entry Form */}
        {isAdmin && (
          <Card
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    size="small"
                    required
                    error={duplicateEntry || (!isEmailValid && email.trim() !== '')}
                    helperText={
                      duplicateEntry
                        ? 'User already has management access'
                        : !isEmailValid && email.trim() !== ''
                          ? 'Email must be @mercedes-benz.com or @msc.corpdir.net'
                          : ''
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Management Level</InputLabel>
                    <Select
                      value={managementLevel}
                      label="Management Level"
                      onChange={(e) => { setManagementLevel(e.target.value); setManagerEmail(''); }}
                    >
                      {MANAGEMENT_LEVELS.map((level) => (
                        <MenuItem key={level} value={level}>
                          {level}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {REPORTS_TO_LEVEL[managementLevel] && (
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Reports to ({REPORTS_TO_LEVEL[managementLevel]})</InputLabel>
                      <Select
                        value={managerEmail}
                        label={`Reports to (${REPORTS_TO_LEVEL[managementLevel]})`}
                        onChange={(e) => setManagerEmail(e.target.value)}
                      >
                        {entries.filter((e) => e.managementLevel === REPORTS_TO_LEVEL[managementLevel]).length === 0 && (
                          <MenuItem value="" disabled>Add a {REPORTS_TO_LEVEL[managementLevel]} first</MenuItem>
                        )}
                        {entries.filter((e) => e.managementLevel === REPORTS_TO_LEVEL[managementLevel]).map((e) => (
                          <MenuItem key={e.email} value={e.email}>{e.email}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={
                      saving ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Plus size={20} />
                      )
                    }
                    onClick={handleAdd}
                    disabled={
                      saving ||
                      duplicateEntry ||
                      !email.trim() ||
                      !isEmailValid ||
                      !managementLevel ||
                      (!!REPORTS_TO_LEVEL[managementLevel] && !managerEmail)
                    }
                    sx={{ height: 40, fontWeight: 600 }}
                  >
                    {saving ? 'Adding...' : 'Add'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Entries Table */}
        <Card
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider' }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Current Access ({visibleEntries.length})
            </Typography>

            {levels.length > 0 && entries.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip
                  label="All"
                  size="small"
                  color={activeLevel === null ? 'primary' : 'default'}
                  variant={activeLevel === null ? 'filled' : 'outlined'}
                  onClick={() => setActiveLevel(null)}
                />
                {levels.map((level) => (
                  <Chip
                    key={level}
                    label={level}
                    size="small"
                    color={activeLevel === level ? 'primary' : 'default'}
                    variant={activeLevel === level ? 'filled' : 'outlined'}
                    onClick={() => setActiveLevel(activeLevel === level ? null : level)}
                  />
                ))}
              </Stack>
            )}
            {entries.length > 0 ? (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Level</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Reports to</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleEntries.map((entry, index) => (
                      <TableRow key={`${entry.email}-${index}`} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {entry.email}
                          </Typography>
                        </TableCell>
                        <TableCell>{entry.managementLevel}</TableCell>
                        <TableCell>{entry.managerEmail || '—'}</TableCell>
                        <TableCell align="right">
                          {isAdmin && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemove(entry)}
                            >
                              <Trash2 size={18} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Card
                elevation={0}
                sx={{ border: '1px dashed', borderColor: 'divider', p: 3 }}
              >
                <Stack alignItems="center" spacing={1}>
                  <ShieldCheck size={36} color={theme.palette.text.disabled} />
                  <Typography variant="body2" color="text.secondary">
                    No management access entries added yet
                  </Typography>
                </Stack>
              </Card>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
